import { observer } from 'mobx-react-lite'
import React from 'react'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts'
import { CREDIT_USAGE_METRICS_CONFIG } from 'entities/Analytics/lib/constants'
import type { CreditUsageChartDataStore } from 'pages/analytics/pages/creditUsage/ui/Chart/store/creditUsageChartDataStore'
import styles from '../styles.module.scss'

interface CreditUsageLineChartProps {
  chartDataStore: CreditUsageChartDataStore
  hiddenMetrics?: Set<string>
}

export const CreditUsageLineChart = observer(
  ({ chartDataStore, hiddenMetrics = new Set() }: CreditUsageLineChartProps) => {
    const { rechartsData } = chartDataStore

    const isOneVisibleMetric =
      hiddenMetrics.size === Object.keys(CREDIT_USAGE_METRICS_CONFIG).length - 1
    const visibleMetricColor = isOneVisibleMetric
      ? Object.entries(CREDIT_USAGE_METRICS_CONFIG).find(([key]) => !hiddenMetrics.has(key))?.[1]
          ?.color
      : ''

    return (
      <div className={styles.chartContainer}>
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={rechartsData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            {isOneVisibleMetric && (
              <defs>
                <linearGradient id='colorGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={visibleMetricColor} stopOpacity={0.3} />
                  <stop offset='100%' stopColor={visibleMetricColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
            )}
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis dataKey='name' interval='preserveStartEnd' minTickGap={1} />
            <YAxis />
            <Tooltip />
            {Object.entries(CREDIT_USAGE_METRICS_CONFIG).map(([key, config]) => {
              if (hiddenMetrics.has(key)) return null

              return (
                <g key={key}>
                  {isOneVisibleMetric && (
                    <Area
                      type='monotone'
                      dataKey={key}
                      stroke={config.color}
                      fill='url(#colorGradient)'
                      strokeWidth={0}
                      tooltipType='none'
                    />
                  )}
                  <Line
                    type='monotone'
                    dataKey={key}
                    stroke={config.color}
                    strokeWidth={2}
                    name={config.name}
                    dot={false}
                  />
                </g>
              )
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
)
